import React, { Dispatch, SetStateAction, useEffect, useState } from "react"
import { FunnelIcon } from "@heroicons/react/24/solid"
import { XMarkIcon } from "@heroicons/react/20/solid"
import { getLocalTimeZone, today as createToday } from '@internationalized/date';
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react"
import { useTranslation } from "react-i18next";
import clsx from "clsx";

import { useRoles } from "src/user/Auth";
import { useLocalState } from "src/settings/localStorage";
import DateRangePicker from "src/components/input/DateRangePicker";

const sortByValues = [
    'internalReference',
    'due',
]

// Keep value at 0 at 'no grouping'/'by family member'
const groupByValues = [
    "by-family-member",
    "by-patent-family",
    "by-claim-scopes",
]


const RenewalsFilterContext = React.createContext({
    onlyUndecided: true as boolean,
    setOnlyUndecided: (() => {}) as Dispatch<SetStateAction<boolean>>,
    groupBy: groupByValues[0], 
    setGroupBy: (() => {}) as Dispatch<SetStateAction<string>>,
    sortBy: sortByValues[0], 
    setSortBy: (() => {}) as Dispatch<SetStateAction<string>>,
    sortOrder: 1 as number, 
    setSortOrder: (() => {}) as Dispatch<SetStateAction<number>>,
    customRange: false as boolean,
    setCustomRange: (() => {}) as Dispatch<SetStateAction<boolean>>,
    minDate: undefined as string,
    setMinDate: (() => {}) as Dispatch<SetStateAction<string>>,
    maxDate: undefined as string,
    setMaxDate: (() => {}) as Dispatch<SetStateAction<string>>,
    smallView: false as boolean,
    setSmallView: (() => {}) as Dispatch<SetStateAction<boolean>>,
    showDetailsFor: (patentFamilyId: number) => true as boolean,
    setShowDetailsFor: (patentFamilyId: number, value: boolean) => {},
})

export function RenewalFilterProvider({children}) {
    const today = createToday(getLocalTimeZone())

    const [groupBy, setGroupBy] = useLocalState('maintenances-group-by', groupByValues[0])
    const [onlyUndecided, setOnlyUndecided] = useLocalState('maintenances-only-undecided', true)

    const [customRange, setCustomRange] = useLocalState('maintenances-only-custom-range', false)
    const [minDate, setMinDate] = useLocalState('maintenances-min-date', today.toString())
    const [maxDate, setMaxDate] = useLocalState('maintenances-max-date', today.add({months: 6}).toString())

    const [sortBy, setSortBy] = useLocalState('maintenances-sorty-by', sortByValues[0])
    const [sortOrder, setSortOrder] = useLocalState('maintenances-sorty-order', -1)

    const [smallView, setSmallView] = useLocalState('maintenances-small-view', false)
    const [showDetails, setShowDetails] = useState({}) // undefined is like true for all; TODO local state

    useEffect(() => {
        setShowDetails(ds => {})
    }, [smallView])

    function showDetailsFor(patentFamilyId: number) {
        if (showDetails && patentFamilyId in showDetails) return showDetails[patentFamilyId]
        else return !smallView
    }

    function setShowDetailsFor(patentFamilyId: number, value: boolean) {
        setShowDetails(d => ({...d, [patentFamilyId]: value}))
    }

    const value = {
        onlyUndecided, setOnlyUndecided,
        groupBy, setGroupBy,
        sortBy, setSortBy,
        sortOrder, setSortOrder,
        customRange, setCustomRange,
        minDate, setMinDate,
        maxDate, setMaxDate, 
        smallView, setSmallView,
        showDetailsFor, setShowDetailsFor,
    }

    return <RenewalsFilterContext.Provider value={value}>
        {children}
    </RenewalsFilterContext.Provider>
}

export function useRenewalsFilter() {
    return React.useContext(RenewalsFilterContext)
}

export function RenewalsFilterTags() {
    const {t} = useTranslation()

    const { groupBy, setGroupBy, onlyUndecided, setOnlyUndecided,
        customRange, setCustomRange, minDate, maxDate, 
     } = useRenewalsFilter()

    const tags = [
        groupBy !== groupByValues[0] &&
        {label: t(groupBy), close: () => setGroupBy(groupByValues[0])},
        !onlyUndecided &&
        {label: 'Include Decided Cases', close: () => setOnlyUndecided(true)},
        customRange && 
        {label: `${minDate} - ${maxDate}`, close: () => setCustomRange(false)},
    ].filter(Boolean)

    return <>{tags.map(({ label, close }) =>
        <div key={label} className="px-1.5 py-1 h-fit bg-pcx-500 hover:bg-pcx-400 text-white text-xs rounded-sm flex flex-row items-center">
            {label}
            <button onClick={close}><XMarkIcon className="h-4 w-4 ml-1" /></button>
        </div>
    )} </>
}

// TODO: Filter date must be for the instruction date and not the fee due date
export function RenewalsFilterMenu() {
    const {t} = useTranslation()

    const today = createToday(getLocalTimeZone())
    const {hasClaimScopes} = useRoles()
    const finalGroupByValues = groupByValues.filter(v => v !== 'by-claim-scopes' || hasClaimScopes)

    const {
        // onlyUndecided, setOnlyUndecided,
        groupBy, setGroupBy, 
        customRange, setCustomRange, minDate, setMinDate, maxDate, setMaxDate,
    } = useRenewalsFilter()

    return (
        <Popover className="relative">
            <PopoverButton className="text-pcx-500 hover:text-pcx-500 p-1">
                <FunnelIcon className="w-4 h-4 " />
            </PopoverButton>
            <PopoverPanel className="absolute mt-1 right-0 z-10 bg-white shadow-lg p-4 rounded-xl border-2 border-pcx-200">
                <div className="grid grid-cols-[auto_auto_auto] gap-x-4 gap-y-1 items-center text-sm">
                    {/* <div className="label">{t('include-decided-cases')}</div>
                    <input type="checkbox" className="form-checkbox" checked={!onlyUndecided} onChange={() => setOnlyUndecided(b => !b)} />
                    <div /> */}

                    <div className="label">{t('grouping')}</div>
                    <div />
                    <select className="form-select text-sm py-1 px-2" value={groupBy} onChange={e => setGroupBy(e.target.value)}>
                        {finalGroupByValues.map(v => <option key={v} value={v}>{t(v)}</option>)}
                    </select>

                    <div className="label normal-case">{t('instruction-due-within')}</div>
                    <input type="checkbox" className="form-checkbox" checked={customRange} onChange={() => setCustomRange(b => !b)} />
                    <DateRangePicker
                        name="date range maintenances"
                        minValue={today}
                        className={clsx("text-sm py-1 px-2 bg-transparent w-fit", !customRange && "opacity-50")}
                        title={t('invoice-due-within')}
                        disabled={!customRange}
                        {...{ minDate, maxDate, setMinDate, setMaxDate }} />
                </div>
            </PopoverPanel>
        </Popover>
    )
}